import { useEffect, useState } from 'react';
import type { SectionTitle } from '../api.ts';

/**
 * The eighty-five seconds of silence, made legible.
 *
 * The pipeline streams three things at very different times. The matrix sweep is pure
 * SQL and lands at about two seconds. Retrieval embeds the file and searches every
 * lender's guideline text, and lands at about five. Then synthesis runs — one model call
 * over the retrieved sections — and lands at about ninety.
 *
 * So for roughly eighty-five seconds the entire product was one spinning circle beside
 * the words "Writing the cited answer". No elapsed time, no indication of what was being
 * read, no statement that a wait this long is normal. Everyone shown the app in that
 * window asks the same question, and it is the correct question: is it hung? A demo in
 * front of a mortgage professional does not survive that, because the honest answer —
 * "it is working, the model is slow" — has to be said out loud by whoever is driving,
 * and anything a human has to apologise for is a defect in the product.
 *
 * Three deliberate refusals, because the obvious fixes are all worse than the silence:
 *
 * No progress bar. Nothing in this system knows what fraction of the answer is written.
 * The model call is opaque until it returns; there is no token stream wired through to
 * here and no partial answer to measure against. A bar would have to be an animation
 * timed to a guess, and a bar that reaches eighty per cent and then stops for forty
 * seconds is a worse lie than a spinner — a spinner never promised anything.
 *
 * No fake activity. It is tempting to cycle the retrieved section titles as though each
 * were being read in turn. We do not know that. They go to the model in one call, all at
 * once, and inventing an order would mean presenting a fabrication about how the product
 * works to the one audience qualified to notice.
 *
 * What is left is the truth, and it turns out to be enough: a clock that moves, the real
 * sections that were really retrieved, and a plain statement of how long this normally
 * takes and why it cannot be more specific than that.
 */

export interface Stage {
  label: string;
  /**
   * `performance.now()` at the moment this stage completed; null while it is still
   * running or has not started. Monotonic rather than `Date.now()` so a machine that
   * corrects its clock mid-search — a laptop waking from sleep on a demo table is the
   * realistic case — cannot produce a negative duration.
   */
  doneAt: number | null;
}

interface Props {
  stages: Stage[];
  /** `performance.now()` at submit. Null before the first search. */
  startedAt: number | null;
  /**
   * The sections retrieval actually returned, from the `sections` event. Null until it
   * lands, and null for a broker whatever has landed — see `detailed`. They are named on
   * screen because they are the specific, checkable thing the wait is buying: which
   * lenders' guidelines are in front of the model before the answer exists.
   */
  sections: SectionTitle[] | null;
  /**
   * The run errored. Stops the clock and, importantly, stops short of ticking the stages
   * that never finished — see the marker logic below.
   */
  failed: boolean;
  /**
   * Whether to explain each stage and name the sections retrieval found. Superuser only.
   *
   * The clock and the three stage labels are NOT behind this, and that is the whole
   * argument of this file: the eighty-five seconds of silence is what makes the product
   * look hung, and it looks hung to a broker exactly as hard as it does to us. What a
   * broker does not need is which seventeen lenders were read and in which sections —
   * that names our panel and our retrieval, not their file.
   */
  detailed: boolean;
}

/**
 * Measured against the dev corpus rather than guessed: verdicts at ~2s, sections at ~5s,
 * the answer at ~90s. TYPICAL is what the copy quotes; LONG is where the copy changes.
 *
 * LONG is 110s rather than 95s because synthesis retries. A draft whose citations do not
 * survive validation against the stored page text is thrown away and written again
 * (`synthesize` allows two attempts), so a perfectly healthy run can take close to twice
 * as long as the normal one. Telling a broker at ninety-five seconds that something is
 * wrong, on a run that is about to answer correctly, trains them to ignore the message
 * on the run where it matters.
 */
const TYPICAL_ANSWER_MS = 90_000;
const LONG_ANSWER_MS = 110_000;

/** How many retrieved sections to name before folding the rest into a count. */
const SECTIONS_SHOWN = 6;

/** Seconds while the number is small enough to read as a number; m:ss once it is not. */
function clock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** A finished stage reports its own duration; for the fast ones the tenths are the point. */
function stageDuration(ms: number): string {
  return ms < 10_000 ? `${(ms / 1000).toFixed(1)}s` : clock(ms);
}

export default function SearchProgress({
  stages, startedAt, sections, failed, detailed,
}: Props) {
  const activeIndex = stages.findIndex((s) => s.doneAt === null);
  const running = startedAt !== null && !failed && activeIndex !== -1;

  // Half-second ticks for a display that only ever shows whole seconds.
  //
  // At a one-second period the interval drifts against the second boundary it is meant
  // to be rendering, and the visible number holds still for stretches approaching two
  // seconds. A counter that stutters is precisely the "is this thing alive" impression
  // this component exists to remove, so it is sampled at twice the rate it is read at.
  const [now, setNow] = useState<number>(() => performance.now());
  useEffect(() => {
    if (!running) return undefined;
    setNow(performance.now());
    const id = window.setInterval(() => setNow(performance.now()), 500);
    return () => window.clearInterval(id);
  }, [running]);

  if (startedAt === null) return null;

  // Once the run is over the total comes from the last stage's own completion stamp, not
  // from the last timer tick — otherwise the figure left on screen is up to half a second
  // long, and it is the one number somebody may well write down.
  const finishedAt = stages[stages.length - 1]?.doneAt;
  const total = (running || finishedAt == null ? now : finishedAt) - startedAt;

  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        marginBottom: 4,
      }}>
        <span className="lbl" style={{ marginBottom: 0 }}>
          {running ? 'Searching' : failed ? 'Stopped' : 'Done'}
        </span>
        <span
          role="timer"
          aria-label={`${Math.floor(total / 1000)} seconds elapsed`}
          style={{
            fontVariantNumeric: 'tabular-nums', fontSize: 15, fontWeight: 600,
            color: running ? 'var(--blue)' : 'var(--muted)',
          }}
        >
          {clock(total)}
        </span>
      </div>

      {stages.map((s, i) => {
        const isActive = i === activeIndex && !failed;
        // A stage that never ran must not be ticked. The error path used to mark every
        // stage done purely to stop the spinners, which left three green checks under a
        // red failure banner — the search read as having completed and then thrown the
        // answer away.
        const marker = s.doneAt !== null ? '✓'
          : isActive ? <span className="spin" />
            : <span aria-hidden="true">·</span>;
        const previous = i === 0 ? startedAt : stages[i - 1]?.doneAt ?? startedAt;
        const spent = s.doneAt !== null ? s.doneAt - previous
          : isActive ? now - previous
            : null;
        const cls = s.doneAt !== null ? ' stage-done'
          : isActive ? ''
            : ' stage-idle';
        return (
          <div key={s.label}>
            <div className={`stage${cls}`}>
              {marker} {s.label}
              {spent !== null ? (
                <span className="stage-time">{stageDuration(spent)}</span>
              ) : null}
            </div>
            {/*
              A broker gets the detail block under the long stage only. The first two are
              two and three seconds; explaining them is colour for us and noise for
              everyone else, whereas the synthesis wait is the one that has to say out
              loud that ninety seconds is normal.
            */}
            {isActive && (detailed || i === stages.length - 1) ? (
              <Detail index={i} elapsed={spent ?? 0}
                      sections={detailed ? sections : null} detailed={detailed} />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/**
 * What is actually happening, under whichever stage is running.
 *
 * Written per stage rather than as one generic sentence, because the three waits are not
 * the same kind of wait and only the third one needs explaining at length.
 */
function Detail(
  { index, elapsed, sections, detailed }:
  { index: number; elapsed: number; sections: SectionTitle[] | null; detailed: boolean },
) {
  if (index === 0) {
    return (
      <div className="stage-detail">
        Every lender&apos;s matrix, every delivery channel, in one SQL pass. This part is
        fast — a couple of seconds.
      </div>
    );
  }

  if (index === 1) {
    return (
      <div className="stage-detail">
        Searching each lender&apos;s guideline text for the sections that bear on this
        file — the carve-outs and overlays a rate grid does not contain.
      </div>
    );
  }

  // `sections` is null for a broker by construction, so the named list below renders
  // nothing for them. Kept as one code path rather than two: a second copy of this block
  // is how the honest "it normally lands around 90 seconds" line ends up drifting out of
  // the view most people are looking at.
  const named = detailed ? sections?.slice(0, SECTIONS_SHOWN) ?? [] : [];
  const hidden = Math.max(0, (sections?.length ?? 0) - named.length);
  const slow = elapsed > LONG_ANSWER_MS;

  return (
    <div className="stage-detail">
      <div>
        {sections && sections.length > 0 ? (
          <>
            Reading <b>{sections.length}</b> guideline section
            {sections.length === 1 ? '' : 's'} and writing the answer, with a citation on
            every claim.
          </>
        ) : (
          <>
            Reading the guideline sections that bear on this file and writing the answer,
            with a citation on every claim.
          </>
        )}
      </div>

      {named.length > 0 ? (
        <ul style={{ margin: '8px 0 0', paddingLeft: 16, color: 'var(--muted)' }}>
          {named.map((s) => (
            <li key={s.citationId} style={{ marginBottom: 2 }}>
              <b style={{ fontWeight: 600, color: 'var(--ink-2)' }}>{s.lenderName}</b>
              {' · '}{s.path}
            </li>
          ))}
          {hidden > 0 ? (
            <li style={{ listStyle: 'none', marginLeft: -16 }}>and {hidden} more</li>
          ) : null}
        </ul>
      ) : null}

      <div style={{ marginTop: 8, color: 'var(--faint)' }}>
        {/* The plain statement. It is here so that nobody in the room has to say it. */}
        This is one model call over those sections, and it cannot report progress from
        inside itself — the clock above is the only honest thing to show. It normally
        lands around {Math.round(TYPICAL_ANSWER_MS / 1000)} seconds.
      </div>

      {slow ? (
        <div style={{ marginTop: 8, color: 'var(--amber)' }}>
          Longer than usual, and nothing has failed. Every quote is checked against the
          stored page text before you see it; a draft that cites something the page does
          not say is discarded and written again, which roughly doubles the wait. It will
          either answer or tell you why.
        </div>
      ) : null}
    </div>
  );
}
